import React, { useEffect, useRef, useCallback } from 'react';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  ColorType,
  CandlestickSeries,
} from 'lightweight-charts';
import { TimeframeSwitcher } from './TimeframeSwitcher';
import { useChart, Timeframe, useTelegram } from '@/hooks';
import { useWebSocket } from '@/hooks';
import { formatPrice } from '@/utils/formatting';

interface CandlestickChartProps {
  height?: number;
}

export const CandlestickChart: React.FC<CandlestickChartProps> = ({ height = 220 }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);

  const { chartData, homeCurrencyData, isLoading, selectedTimeframe, setTimeframe } = useChart();
  const { mainCurrencyPrice } = useWebSocket();
  const { colorScheme } = useTelegram();

  // Theme colors based on color scheme
  const isDark = colorScheme === 'dark';
  const theme = {
    background: isDark
      ? 'linear-gradient(180deg, #0F0F0F 0%, #212121 100%)'
      : 'linear-gradient(180deg, #F8F8F8 0%, #FFFFFF 100%)',
    textColor: isDark ? '#FFFFFF' : '#000000',
    hintColor: isDark ? '#8E8E93' : '#6D6D71',
    gridColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    crosshairColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
  };

  // Use live price from WebSocket, fallback to API price
  const currentPrice = mainCurrencyPrice || homeCurrencyData?.current_price;
  const priceChange = homeCurrencyData?.price_change_percentage_24h || 0;
  const isPositive = priceChange >= 0;

  // Initialize chart
  const initChart = useCallback(() => {
    if (!chartContainerRef.current) return;

    // Clean up existing chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      candlestickSeriesRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: theme.hintColor,
        attributionLogo: false, // Remove TradingView logo
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: theme.gridColor },
      },
      crosshair: {
        mode: 0, // Normal mode
        vertLine: {
          width: 1,
          color: theme.crosshairColor,
          style: 2,
        },
        horzLine: {
          width: 1,
          color: theme.crosshairColor,
          style: 2,
        },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
      },
      handleScale: false,
      handleScroll: false,
    });

    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#72EB89',
      downColor: '#EB7272',
      borderUpColor: '#72EB89',
      borderDownColor: '#EB7272',
      wickUpColor: '#72EB89',
      wickDownColor: '#EB7272',
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [height, theme.hintColor, theme.gridColor, theme.crosshairColor]);

  // Initialize chart on mount
  useEffect(() => {
    const cleanup = initChart();
    return () => {
      cleanup?.();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candlestickSeriesRef.current = null;
      }
    };
  }, [initChart]);

  // Update chart data
  useEffect(() => {
    if (candlestickSeriesRef.current && chartData.length > 0) {
      const formattedData: CandlestickData[] = chartData.map((candle) => ({
        time: candle.time as CandlestickData['time'],
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      }));

      candlestickSeriesRef.current.setData(formattedData);

      // Fit content to view
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
      }
    }
  }, [chartData]);

  const handleTimeframeChange = (timeframe: Timeframe) => {
    setTimeframe(timeframe);
  };

  return (
    <div
      style={{
        background: theme.background,
        borderRadius: '16px',
        overflow: 'hidden',
        marginBottom: '16px',
      }}
    >
      {/* Header with BTC info */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
        }}
      >
        {/* Left side - BTC info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#F7931A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            ₿
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: theme.textColor }}>BTC</div>
            <div style={{ fontSize: '12px', color: theme.hintColor }}>Bitcoin</div>
          </div>
        </div>

        {/* Right side - Price and change */}
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '4px',
              marginBottom: '2px',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                color: isPositive ? '#72EB89' : '#EB7272',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
              }}
            >
              <span style={{ fontSize: '10px' }}>{isPositive ? '▲' : '▼'}</span>
              {Math.abs(priceChange).toFixed(2)}%
            </span>
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: theme.textColor }}>
            USDT {currentPrice ? formatPrice(currentPrice) : '...'}
          </div>
        </div>
      </div>

      {/* Chart container */}
      <div
        ref={chartContainerRef}
        style={{
          width: '100%',
          height: `${height}px`,
          position: 'relative',
        }}
      >
        {isLoading && chartData.length === 0 && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: theme.hintColor,
              fontSize: '14px',
            }}
          >
            Loading chart...
          </div>
        )}
      </div>

      {/* Timeframe switcher */}
      <TimeframeSwitcher selected={selectedTimeframe} onSelect={handleTimeframeChange} />
    </div>
  );
};
